import { Link } from "react-router-dom";
import Button from "@/components/base/Button";
import { clinicInfo } from "@/mocks/site";
import { galleryItems } from "@/data/gallery";

/**
 * Premium editorial hero for the About page — a soft blue / cyan / violet
 * canvas with a layered image composition and a floating credential card.
 */
export default function AboutHero() {
  const bigImage = galleryItems[1];
  const smallImage = galleryItems[5];

  return (
    <section className="relative overflow-hidden bg-background-50">
      <div
        className="absolute inset-0 bg-gradient-to-br from-primary-50 via-background-50 to-accent-50"
        aria-hidden="true"
      ></div>
      <div
        className="absolute inset-0 bg-gradient-to-tr from-secondary-50/80 via-transparent to-accent-50/70"
        aria-hidden="true"
      ></div>
      <div
        className="absolute inset-0 opacity-[0.25] [background-image:linear-gradient(to_right,oklch(var(--background-200))_1px,transparent_1px),linear-gradient(to_bottom,oklch(var(--background-200))_1px,transparent_1px)] [background-size:76px_76px]"
        aria-hidden="true"
      ></div>
      <div className="absolute -left-24 -top-24 h-[28rem] w-[28rem] rounded-full bg-primary-200/40 blur-3xl" aria-hidden="true"></div>
      <div className="absolute -right-24 top-8 h-[26rem] w-[26rem] rounded-full bg-accent-200/40 blur-3xl" aria-hidden="true"></div>
      <div className="absolute bottom-0 left-1/3 h-[22rem] w-[22rem] rounded-full bg-secondary-200/40 blur-3xl" aria-hidden="true"></div>

      <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-10 md:px-6 md:pb-24 md:pt-14">
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            <li className="flex items-center gap-2">
              <Link to="/" className="transition-colors hover:text-primary-700">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
            </li>
            <li>
              <span className="text-foreground-800">About</span>
            </li>
          </ol>
        </nav>

        <div className="mt-8 grid grid-cols-1 items-center gap-x-12 gap-y-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <span className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50 px-3.5 py-1.5 font-label text-xs font-semibold uppercase tracking-[0.16em] text-accent-700">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500" aria-hidden="true"></span>
              Who We Are
            </span>

            <h1 className="animate-fade-up mt-6 font-heading text-[2.4rem] font-semibold leading-[1.05] tracking-tight text-foreground-950 md:text-5xl lg:text-[3.4rem]">
              About <span className="text-gradient-brand">Ortho Spine Care</span>
            </h1>

            <p className="animate-fade-up mt-6 max-w-xl font-body text-base leading-relaxed text-foreground-600 md:text-lg">
              A dedicated orthopaedic and spine surgery practice in Srirangam, Trichy — built around accurate diagnosis,
              clear communication and personalised care for every patient.
            </p>

            <div className="animate-fade-up mt-8 flex flex-col gap-3 sm:flex-row">
              <Button to="/appointment" size="lg" icon="ri-calendar-check-line">
                Book an Appointment
              </Button>
              <Button to="/about#specialist" variant="outline" size="lg" icon="ri-user-heart-line">
                Meet Our Doctor
              </Button>
            </div>

            <div className="animate-fade-up mt-9 flex flex-wrap items-center gap-x-7 gap-y-4">
              <a
                href={clinicInfo.phoneHref}
                className="flex items-center gap-2.5 font-label text-sm font-semibold text-foreground-700 transition-colors hover:text-primary-700"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-phone-fill" aria-hidden="true"></i>
                </span>
                {clinicInfo.phoneDisplay}
              </a>
              <span className="flex items-center gap-2.5 font-label text-sm font-medium text-foreground-600">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-map-pin-line" aria-hidden="true"></i>
                </span>
                Srirangam, Trichy
              </span>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative mx-auto w-full max-w-[500px]">
              <div
                className="absolute -inset-x-6 -top-6 bottom-16 rounded-[2.75rem] bg-gradient-to-br from-primary-200/50 via-secondary-200/40 to-accent-200/50"
                aria-hidden="true"
              ></div>
              <div
                className="absolute -left-4 top-10 h-20 w-20 rounded-full border border-accent-200 bg-accent-50/60"
                aria-hidden="true"
              ></div>
              <div className="absolute -bottom-4 right-10 h-14 w-14 rounded-full bg-secondary-200/70" aria-hidden="true"></div>

              <div className="relative overflow-hidden rounded-[2.5rem] border border-background-200 bg-background-100 shadow-[0_40px_90px_-50px_rgba(15,40,80,0.55)]">
                <div className="h-[420px] w-full md:h-[540px]">
                  <img
                    src={bigImage.image}
                    alt={bigImage.alt}
                    title={`${bigImage.title} — Ortho Spine Care, Trichy`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div
                  className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-primary-950/45 to-transparent"
                  aria-hidden="true"
                ></div>
              </div>

              <div className="absolute -bottom-8 -left-5 h-40 w-32 overflow-hidden rounded-3xl border-4 border-background-50 shadow-[0_25px_50px_-30px_rgba(15,40,80,0.6)] md:h-48 md:w-36">
                <img
                  src={smallImage.image}
                  alt={smallImage.alt}
                  title={`${smallImage.title} — Ortho Spine Care, Trichy`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="animate-float absolute -right-3 top-8 flex w-[15rem] items-center gap-3 rounded-2xl border border-background-200 bg-background-50/95 p-3.5 shadow-[0_20px_45px_-28px_rgba(15,40,80,0.55)] backdrop-blur md:-right-6">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-gradient-to-br from-primary-500 to-accent-500 text-background-50">
                  <i className="ri-shield-check-line text-xl" aria-hidden="true"></i>
                </span>
                <span className="min-w-0">
                  <span className="block font-heading text-sm font-semibold text-foreground-950">Specialist Orthopaedic</span>
                  <span className="block font-label text-xs text-foreground-600">&amp; Spine Care · Trichy</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}